interface HourlyForecast {
  time: string;
  temp: number;
  icon: string;
  precip_mm?: number;
}

interface DailyForecast {
  date: string;
  day_name: string;
  high: number;
  low: number;
  icon: string;
  description: string;
}

interface WeatherData {
  location: string;
  current_temp: number;
  feels_like: number;
  description: string;
  icon: string;
  humidity: number;
  wind_kph: number;
  uv_index?: number;
  hourly?: HourlyForecast[];
  daily?: DailyForecast[];
  unit?: "C" | "F";
}

interface Props {
  data: WeatherData;
}

const WEATHER_ICONS: Record<string, string> = {
  "clear-day": "☀️", "clear-night": "🌙",
  "partly-cloudy-day": "⛅", "partly-cloudy-night": "🌤️",
  "cloudy": "☁️", "rain": "🌧️", "drizzle": "🌦️",
  "snow": "❄️", "sleet": "🌨️", "wind": "💨",
  "fog": "🌫️", "thunder": "⛈️", "hail": "🌩️",
};

function WeatherIcon({ code, size = 32 }: { code: string; size?: number }) {
  const emoji = WEATHER_ICONS[code] || "🌡️";
  return <span style={{ fontSize: size }}>{emoji}</span>;
}

export default function WeatherCard({ data }: Props) {
  const unit = data.unit || "C";

  return (
    <div className="tool-weather">
      <div className="weather-main">
        <div className="weather-location">{data.location}</div>
        <div className="weather-current">
          <WeatherIcon code={data.icon} size={48} />
          <div className="weather-temp-block">
            <span className="weather-temp">{Math.round(data.current_temp)}°{unit}</span>
            <span className="weather-feels">Føles som {Math.round(data.feels_like)}°</span>
          </div>
        </div>
        <div className="weather-desc">{data.description}</div>
        <div className="weather-stats">
          <span>💧 {data.humidity}%</span>
          <span>💨 {Math.round(data.wind_kph)} km/t</span>
          {data.uv_index !== undefined && <span>☀️ UV {data.uv_index}</span>}
        </div>
      </div>

      {data.hourly && data.hourly.length > 0 && (
        <div className="weather-hourly">
          {data.hourly.slice(0, 8).map((h, i) => (
            <div key={i} className="weather-hourly-item">
              <span className="weather-hourly-time">{h.time}</span>
              <WeatherIcon code={h.icon} size={20} />
              <span className="weather-hourly-temp">{Math.round(h.temp)}°</span>
              {h.precip_mm !== undefined && h.precip_mm > 0 && (
                <span className="weather-hourly-precip">{h.precip_mm}mm</span>
              )}
            </div>
          ))}
        </div>
      )}

      {data.daily && data.daily.length > 0 && (
        <div className="weather-daily">
          {data.daily.slice(0, 7).map((d, i) => (
            <div key={i} className="weather-daily-item">
              <span className="weather-daily-day">{d.day_name}</span>
              <WeatherIcon code={d.icon} size={18} />
              <span className="weather-daily-desc">{d.description}</span>
              <span className="weather-daily-range">
                <span className="weather-daily-high">{Math.round(d.high)}°</span>
                <span className="weather-daily-low">{Math.round(d.low)}°</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
